import React from "react";

import {
  ArrowRight,
  CheckCircle2,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  LogIn,
  Mail,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

import { Link, useParams } from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../../../components/LanguageSwitcher";
import backgroundImg from "../../../assets/background.png";
import logoImg from "../../../assets/logo.png";

import "./PlatformAdminInvitationPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   PAGE FRAME
   ========================================================= */

function AdminInvitationPageFrame({ children, compact = false }) {
  return (
    <div className="admin-invitation-page">
      <div
        className="admin-invitation-page__bg-illustration"
        style={{
          backgroundImage: `url(${backgroundImg})`,
        }}
      />

      <header className="admin-invitation-page__topbar">
        <Link
          to="/"
          className="admin-invitation-page__brand"
          aria-label="LEG-UP"
        >
          <img src={logoImg} alt="LEG-UP" />
        </Link>

        <LanguageSwitcher />
      </header>

      <main
        className={`admin-invitation-page__shell ${
          compact ? "admin-invitation-page__shell--compact" : ""
        }`}
      >
        {children}
      </main>
    </div>
  );
}

/* =========================================================
   PLATFORM ADMIN INVITATION PAGE
   ========================================================= */

export default function PlatformAdminInvitationPage() {
  const { token } = useParams();

  const { t } = useTranslation("platform-admin-invitation");

  const { loginWithRedirect } = useAuth0();

  /* =========================================================
     STATE
     ========================================================= */

  const [invitation, setInvitation] = React.useState(null);

  const [loadingInvitation, setLoadingInvitation] = React.useState(true);

  const [startingSetup, setStartingSetup] = React.useState(false);

  const [error, setError] = React.useState("");

  const [errorCode, setErrorCode] = React.useState("");

  /* =========================================================
     VALIDATE INVITATION
     ========================================================= */

  React.useEffect(() => {
    let cancelled = false;

    async function validateInvitation() {
      if (!token) {
        setErrorCode("invalid_invitation");

        setError(
          t("errors.missingToken", "The invitation link is incomplete."),
        );

        setLoadingInvitation(false);

        return;
      }

      try {
        setLoadingInvitation(true);
        setError("");
        setErrorCode("");

        const response = await fetch(
          `${API_BASE}/api/admin/invitations/${encodeURIComponent(token)}/`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data?.valid === false) {
          if (!cancelled) {
            setErrorCode(data?.error || "invalid_invitation");

            setError(
              data?.message ||
                data?.detail ||
                t(
                  "errors.invalidInvitation",
                  "This invitation is not valid or is no longer active.",
                ),
            );
          }

          return;
        }

        if (!cancelled) {
          setInvitation(data);
        }
      } catch (requestError) {
        if (!cancelled) {
          setErrorCode("network_error");

          setError(
            requestError?.message ||
              t(
                "errors.validationFailed",
                "The invitation could not be validated.",
              ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingInvitation(false);
        }
      }
    }

    validateInvitation();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  /* =========================================================
     START ADMIN ACCOUNT SETUP
     ========================================================= */

  async function handleAccountSetup() {
    if (!token || startingSetup) {
      return;
    }

    try {
      setStartingSetup(true);
      setError("");
      setErrorCode("");

      const response = await fetch(
        `${API_BASE}/api/admin/invitations/${encodeURIComponent(token)}/setup/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.detail ||
            t(
              "errors.setupFailed",
              "The administrator account could not be prepared.",
            ),
        );
      }

      if (!data?.password_setup_url) {
        throw new Error(
          t(
            "errors.missingSetupUrl",
            "The secure password setup link could not be generated.",
          ),
        );
      }

      window.location.assign(data.password_setup_url);
    } catch (requestError) {
      setErrorCode("setup_failed");

      setError(
        requestError?.message ||
          t(
            "errors.setupFailed",
            "The administrator account could not be prepared.",
          ),
      );
    } finally {
      setStartingSetup(false);
    }
  }

  /* =========================================================
     EXISTING / COMPLETED ACCOUNT LOGIN
     ========================================================= */

  async function handleLogin() {
    await loginWithRedirect({
      appState: {
        returnTo: "/admin/dashboard",
      },
      authorizationParams: {
        screen_hint: "login",

        ...(invitation?.email
          ? {
              login_hint: invitation.email,
            }
          : {}),
      },
    });
  }

  /* =========================================================
     STATE CONTENT
     ========================================================= */

  function getUnavailableContent() {
    if (errorCode === "invitation_expired") {
      return {
        eyebrow: t("expired.eyebrow", "Invitation expired"),

        title: t("expired.title", "This invitation has expired"),

        help: t(
          "expired.help",
          "Please contact the LEG-UP platform team if you still require administrator access.",
        ),
      };
    }

    if (errorCode === "invitation_revoked") {
      return {
        eyebrow: t("revoked.eyebrow", "Invitation unavailable"),

        title: t("revoked.title", "This invitation is no longer active"),

        help: t(
          "revoked.help",
          "The administrator invitation has been revoked and can no longer be used.",
        ),
      };
    }

    if (errorCode === "invitation_accepted") {
      return {
        eyebrow: t("accepted.eyebrow", "Account already configured"),

        title: t("accepted.title", "This invitation has already been accepted"),

        help: t(
          "accepted.help",
          "If your administrator account has already been configured, you can continue to LEG-UP login.",
        ),
      };
    }

    return {
      eyebrow: t("invalid.eyebrow", "Invitation unavailable"),

      title: t("invalid.title", "We could not open this invitation"),

      help: t(
        "invalid.help",
        "The invitation may have expired, already been accepted, or the link may be incorrect.",
      ),
    };
  }

  /* =========================================================
     LOADING
     ========================================================= */

  if (loadingInvitation) {
    return (
      <AdminInvitationPageFrame compact>
        <section className="admin-invitation-page__card admin-invitation-page__card--state">
          <div className="admin-invitation-page__loader">
            <LoaderCircle size={30} />
          </div>

          <span className="admin-invitation-page__eyebrow">
            {t("loading.eyebrow", "Secure invitation")}
          </span>

          <h1>{t("loading.title", "Checking your invitation")}</h1>

          <p className="admin-invitation-page__lead">
            {t(
              "loading.description",
              "Please wait while we securely verify your administrator invitation.",
            )}
          </p>
        </section>
      </AdminInvitationPageFrame>
    );
  }

  /* =========================================================
     INVALID / EXPIRED / ACCEPTED INVITATION
     ========================================================= */

  if (error && !invitation) {
    const content = getUnavailableContent();

    return (
      <AdminInvitationPageFrame compact>
        <section className="admin-invitation-page__card admin-invitation-page__card--state">
          <div className="admin-invitation-page__status-icon admin-invitation-page__status-icon--error">
            <TriangleAlert size={29} />
          </div>

          <span className="admin-invitation-page__eyebrow">
            {content.eyebrow}
          </span>

          <h1>{content.title}</h1>

          <p className="admin-invitation-page__lead">{error}</p>

          <div className="admin-invitation-page__state-divider" />

          <p className="admin-invitation-page__secondary-copy">
            {content.help}
          </p>

          {errorCode === "invitation_accepted" ? (
            <button
              type="button"
              className="admin-invitation-page__button admin-invitation-page__button--primary admin-invitation-page__state-button"
              onClick={handleLogin}
            >
              <LogIn size={17} />

              <span>{t("actions.signIn", "Sign in to LEG-UP")}</span>

              <ArrowRight size={17} />
            </button>
          ) : null}
        </section>
      </AdminInvitationPageFrame>
    );
  }

  /* =========================================================
     MAIN PAGE
     ========================================================= */

  return (
    <AdminInvitationPageFrame>
      <section className="admin-invitation-page__card">
        {/* =================================================
            STATUS
            ================================================= */}

        <header className="admin-invitation-page__card-header">
          <div className="admin-invitation-page__status">
            <ShieldCheck size={14} />

            <span>{t("badge", "Secure invitation")}</span>
          </div>
        </header>

        {/* =================================================
            HERO
            ================================================= */}

        <div className="admin-invitation-page__hero">
          <div className="admin-invitation-page__hero-icon">
            <ShieldCheck size={27} />
          </div>

          <span className="admin-invitation-page__eyebrow">
            {t("eyebrow", "Platform administrator invitation")}
          </span>

          <h1>{t("title", "Set up your administrator account")}</h1>

          <p className="admin-invitation-page__lead">
            {t(
              "description",
              "You have been invited to join LEG-UP as a Platform Administrator. Complete the secure account setup to access the platform administration area.",
            )}
          </p>
        </div>

        {/* =================================================
            DETAILS
            ================================================= */}

        <div className="admin-invitation-page__details">
          <div className="admin-invitation-page__detail">
            <div className="admin-invitation-page__detail-icon">
              <Mail size={18} />
            </div>

            <div>
              <span>{t("details.invitedEmail", "Invited email")}</span>

              <strong>{invitation?.email || "—"}</strong>
            </div>
          </div>

          <div className="admin-invitation-page__detail">
            <div className="admin-invitation-page__detail-icon">
              <KeyRound size={18} />
            </div>

            <div>
              <span>{t("details.accessLevel", "Access level")}</span>

              <strong>
                {t("details.platformAdministrator", "Platform Administrator")}
              </strong>
            </div>
          </div>
        </div>

        {/* =================================================
            SECURITY INFORMATION
            ================================================= */}

        <div className="admin-invitation-page__info">
          <LockKeyhole size={18} />

          <p>
            {t(
              "security",
              "Your password and multi-factor authentication will be configured securely through the LEG-UP authentication service. Your invited email address cannot be changed.",
            )}
          </p>
        </div>

        {/* =================================================
            SETUP ERROR
            ================================================= */}

        {error ? (
          <div className="admin-invitation-page__error" role="alert">
            <TriangleAlert size={17} />

            <span>{error}</span>
          </div>
        ) : null}

        {/* =================================================
            ACTIONS
            ================================================= */}

        <div className="admin-invitation-page__actions">
          <button
            type="button"
            className="admin-invitation-page__button admin-invitation-page__button--primary"
            onClick={handleAccountSetup}
            disabled={startingSetup}
          >
            {startingSetup ? (
              <LoaderCircle
                size={17}
                className="admin-invitation-page__spinner"
              />
            ) : (
              <CheckCircle2 size={17} />
            )}

            <span>
              {startingSetup
                ? t("actions.preparing", "Preparing your account...")
                : invitation?.setup_started
                  ? t("actions.continueSetup", "Continue account setup")
                  : t("actions.setup", "Set up my account")}
            </span>

            {!startingSetup ? <ArrowRight size={17} /> : null}
          </button>

          <p className="admin-invitation-page__action-help">
            {t(
              "actions.setupHelp",
              "You will be redirected to the secure LEG-UP authentication service to create your password.",
            )}
          </p>

          <button
            type="button"
            className="admin-invitation-page__sign-in-link"
            onClick={handleLogin}
          >
            {t(
              "actions.alreadyConfigured",
              "Already completed your account setup? Sign in",
            )}
          </button>
        </div>

        {/* =================================================
            FOOTER
            ================================================= */}

        <footer className="admin-invitation-page__footer">
          <ShieldCheck size={14} />

          <span>
            {t(
              "footer",
              "Your administrator account is protected by LEG-UP secure authentication.",
            )}
          </span>
        </footer>
      </section>
    </AdminInvitationPageFrame>
  );
}
